/**
 * P41 · point 3 — the academy's HALF of the double opt-in: asking the
 * guardian for consent by email.
 *
 * Without this piece the flow had no door into it from the interface: the
 * public page `/guardian-consent` existed but no one could generate the link
 * leading to it without calling the endpoint by hand.
 *
 * ── Why the link is shown on screen ──────────────────────────────────────────
 *
 * **There's no email to send yet.** The message body is legal copy
 * and is pending from the owner (`PENDIENTE-TEXTO-CONSENTIMIENTO-DUENO`), so
 * the server returns the link in the response so the academy can get it
 * across however it can. That exposes nothing: only whoever just created the
 * request sees it, and creating it already requires `ManageStudents` over that academy.
 * The day the template exists, the field disappears from the response and this
 * block with it.
 *
 * ── The scope is chosen, not assumed ─────────────────────────────────────────
 *
 * The five scopes are a closed catalog and the server validates them STRICTLY
 * on write: a string it doesn't recognize is an error, not a silent drop.
 * That's why here they're checkboxes over the same catalog and not a text field.
 *
 * None of this actually enforces anything: `academy.guardian_consent` is the record
 * (collect and prove), and `academy.guardian_consent.write_gates` —the switch
 * that actually silences someone— is a different one and is born off just the same.
 */
import { msg } from "@lingui/core/macro";
import { type Component, createSignal, For, Show } from "solid-js";
import { handleApiResponse } from "@space/shared";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import {
  CONSENT_SCOPE_LABELS,
  DEFAULT_CONSENT_SCOPES,
  labelFor,
  OFFERED_CONSENT_SCOPES,
} from "./labels";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

export const RequestConsentPanel: Component<{
  organizationId: string;
  studentUserId: string;
}> = (props) => {
  const { t } = useI18n();
  const consent = useFeatureEnabled("academy.guardian_consent");

  const [email, setEmail] = createSignal("");
  const [relationship, setRelationship] = createSignal("");
  const [scopes, setScopes] = createSignal<string[]>([...DEFAULT_CONSENT_SCOPES]);
  const [busy, setBusy] = createSignal(false);
  const [link, setLink] = createSignal<string | null>(null);

  const toggle = (scope: string) => {
    setScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );
  };

  const send = async () => {
    if (busy() || !email().trim() || scopes().length === 0) {
      return;
    }
    setBusy(true);
    try {
      const created = await handleApiResponse(
        api.POST("/api/org/guardianConsent/request", {
          body: {
            organizationId: props.organizationId,
            studentUserId: props.studentUserId,
            guardianEmail: email().trim(),
            relationship: relationship().trim() || null,
            scopes: scopes(),
          },
        }),
      );
      setLink(created.confirmUrl);
      showToast(t(msg`Request created`), "success");
    } catch {
      showToast(t(msg`Could not create the request`), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Show when={consent().enabled}>
      <div class="space-y-3 rounded-xl border border-border p-3" data-testid="consent-request">
        <div>
          <h4 class="text-sm font-semibold">{t(msg`Ask the guardian for authorization`)}</h4>
          <p class="text-xs text-muted-foreground">
            {t(
              msg`The guardian confirms it themselves from a single-use link. Nothing is authorized until they do.`,
            )}
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <label class="flex-1 space-y-1 text-xs text-muted-foreground">
            {t(msg`Guardian's email`)}
            <input
              class={field}
              type="email"
              data-testid="consent-request-email"
              value={email()}
              onInput={(e) => {
                setEmail(e.currentTarget.value);
              }}
            />
          </label>
          <label class="flex-1 space-y-1 text-xs text-muted-foreground">
            {t(msg`Relationship`)}
            <input
              class={field}
              data-testid="consent-request-relationship"
              value={relationship()}
              onInput={(e) => {
                setRelationship(e.currentTarget.value);
              }}
              placeholder={t(msg`e.g. Mother`)}
            />
          </label>
        </div>

        <fieldset class="space-y-2">
          <legend class="text-xs text-muted-foreground">{t(msg`What is being asked for`)}</legend>
          <div class="flex flex-wrap gap-3">
            <For each={OFFERED_CONSENT_SCOPES}>
              {(scope) => (
                <Show when={labelFor(CONSENT_SCOPE_LABELS, scope)}>
                  {(label) => (
                    <label class="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        data-testid={`consent-scope-${scope}`}
                        checked={scopes().includes(scope)}
                        onChange={() => {
                          toggle(scope);
                        }}
                      />
                      {t(label())}
                    </label>
                  )}
                </Show>
              )}
            </For>
          </div>
        </fieldset>

        <Button
          data-testid="consent-request-send"
          disabled={busy() || !email().trim() || scopes().length === 0}
          onClick={() => void send()}
        >
          {t(msg`Create request`)}
        </Button>

        <Show when={link()}>
          {(url) => (
            <div class="space-y-1" data-testid="consent-request-link">
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`There is no automatic email yet: send this link to the guardian. It works once and expires.`,
                )}
              </p>
              <code class="block break-all rounded-lg bg-muted p-2 text-xs">{url()}</code>
            </div>
          )}
        </Show>
      </div>
    </Show>
  );
};
